import { useMemo, useState } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Menu } from "@/components/ui/menu"
import {
  IdCard,
  MoreVertical,
  Printer,
  PrinterCheck,
  PrinterX,
  Settings2,
  Table2,
} from "lucide-react"
import { toast } from "sonner"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import { BadgeSheet } from "@/components/people/BadgeSheet"
import { BadgeEditModal } from "@/components/people/BadgeEditPane"
import { BulkEditSheet, type GuestCol } from "@/components/people/BulkEditSheet"
import { useRightRail } from "@/lib/right-rail"

// Badge-list bulk edit: JUST the badge name, prefilled from the saved badge
// name or — when empty — the legal first/last name. Untouched cells commit
// nothing (the display fallback covers them); edits diff against the prefill.
const BADGE_EDIT_COLS: GuestCol[] = [
  { key: "badgeFirstName", label: "Badge first name", kind: "text", fallback: (g) => g.firstName ?? "" },
  { key: "badgeLastName", label: "Badge last name", kind: "text", fallback: (g) => g.lastName ?? "" },
]
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  attendeeSetupQueryOptions,
  badgeSettingsQueryOptions,
  displayStatus,
  eventAttendeeFieldValuesQueryOptions,
  guestDisplayName,
  guestsKey,
  joinName,
  guestsQueryOptions,
  markBadgePrinted,
  patchGuestCaches,
  statusLabel,
  STATUS_BADGE_CLS,
  type Guest,
} from "@/core/people"

// Printable name badges over the guest roster. Selection + filters via the
// house CollectionView; "Print badges" opens the BadgeSheet (4×3" cards, 6 per
// Letter sheet) and stamps badgePrintedAt after the print dialog. Default view
// = confirmed guests without a badge yet, so the desk workflow is
// select-all → print. The badge QR is the staff-only guest-record link — see
// the SECURITY note in BadgeSheet.

export const Route = createFileRoute("/$orgSlug/$eventSlug/badges")({
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(guestsQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(attendeeSetupQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(eventAttendeeFieldValuesQueryOptions(eventId))
        .catch(() => undefined)
      void context.queryClient
        .ensureQueryData(badgeSettingsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: BadgesView,
})

// Per-row ⋮ menu — the same verbs as the bulk bar, scoped to one guest.
function RowActions({
  guest: g,
  onPrint,
  onEditNames,
  onMark,
  onClear,
}: {
  guest: Guest
  onPrint: () => void
  onEditNames: () => void
  onMark: () => void
  onClear: () => void
}) {
  return (
    <Menu.Root>
      <Menu.Trigger
        onClick={(e) => e.stopPropagation()}
        title="Badge actions"
        className="grid h-7 w-7 place-items-center rounded-md text-plum-700/55 transition hover:bg-plum-100/60 hover:text-plum-900 dark:text-plum-200/45 dark:hover:bg-white/5 dark:hover:text-plum-50"
      >
        <MoreVertical className="h-4 w-4" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={4} align="end" className="z-[60] outline-none">
          <Menu.Popup className="mb-pop min-w-[11rem]">
            <Menu.Item className="mb-mi" onClick={onPrint}>
              <Printer className="h-4 w-4" />
              <span className="flex-1">Print badge</span>
            </Menu.Item>
            <Menu.Item className="mb-mi" onClick={onEditNames}>
              <Table2 className="h-4 w-4" />
              <span className="flex-1">Edit badge name</span>
            </Menu.Item>
            <Menu.Item className="mb-mi" onClick={onMark}>
              <PrinterCheck className="h-4 w-4" />
              <span className="flex-1">Mark printed</span>
            </Menu.Item>
            {g.badgePrintedAt && (
              <Menu.Item className="mb-mi" onClick={onClear}>
                <PrinterX className="h-4 w-4" />
                <span className="flex-1">Clear print date</span>
              </Menu.Item>
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}

const legalName = (g: Guest) => joinName(g.firstName, g.lastName)

function dateOnly(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString()
}

function dateTime(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
}

function BadgesView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/badges" })
  const navigate = useNavigate()
  const { eventId, row: eventRow } = useActiveEvent()
  const [printing, setPrinting] = useState<Guest[] | null>(null)
  const showInRail = useRightRail((s) => s.show)
  const [bulkEdit, setBulkEdit] = useState<Guest[] | null>(null)
  const qc = useQueryClient()

  // Manual mark-printed / clear-print-date over the selection. Optimistic:
  // patch the caches immediately, roll back the rows whose request failed,
  // then invalidate so everything settles on server truth.
  const runMarkPrinted = async (rows: Guest[], clear: boolean) => {
    const now = new Date().toISOString()
    rows.forEach((g) =>
      patchGuestCaches(qc, g.id, { badgePrintedAt: clear ? null : now }),
    )
    const results = await Promise.allSettled(
      rows.map((g) => markBadgePrinted(g.id, { clear })),
    )
    results.forEach((r, i) => {
      if (r.status === "rejected")
        patchGuestCaches(qc, rows[i].id, { badgePrintedAt: rows[i].badgePrintedAt ?? null })
    })
    const ok = results.filter((r) => r.status === "fulfilled").length
    if (eventId) void qc.invalidateQueries({ queryKey: guestsKey(eventId) })
    const verb = clear ? "cleared" : "marked printed"
    if (ok === results.length) toast.success(`${ok} badge${ok === 1 ? "" : "s"} ${verb}`)
    else toast.error(`${ok}/${results.length} ${verb} — the rest failed`)
  }
  const [badgeEdit, setBadgeEdit] = useState<Guest | null>(null)

  const guestsQ = useQuery({
    ...guestsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const guests = guestsQ.data ?? []

  // Attendee custom fields (Table, Track, …) — offered as optional badge lines.
  const setupQ = useQuery({
    ...attendeeSetupQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const attendeeFields = setupQ.data?.fields ?? []
  const valuesQ = useQuery({
    ...eventAttendeeFieldValuesQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const settingsQ = useQuery({
    ...badgeSettingsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const valuesByGuest = useMemo(() => {
    const optionLabel = new Map(
      attendeeFields.flatMap((f) => (f.options ?? []).map((o) => [`${f.id}:${o.value}`, o.label])),
    )
    const map = new Map<string, Record<string, string>>()
    for (const v of valuesQ.data?.data ?? []) {
      if (v.value == null || v.value === "") continue
      const rec = map.get(v.guestId) ?? {}
      rec[v.fieldId] = optionLabel.get(`${v.fieldId}:${v.value}`) ?? v.value
      map.set(v.guestId, rec)
    }
    return map
  }, [attendeeFields, valuesQ.data])

  const tierNames = useMemo(
    () =>
      [...new Set(guests.map((g) => g.ticketTypeName).filter(Boolean))] as string[],
    [guests],
  )

  const config = useMemo<CollectionConfig<Guest>>(
    () => ({
      title: "Badges",
      getId: (g) => g.id,
      searchText: (g) =>
        [
          guestDisplayName(g),
          g.emailAtInvite,
          g.companyAtInvite ?? "",
          g.titleAtInvite ?? "",
          g.ticketTypeName ?? "",
        ].join(" "),
      columns: [
        {
          id: "name",
          header: "Guest",
          grow: true,
          truncate: true,
          sortable: true,
          sortValue: (g) => guestDisplayName(g).toLowerCase(),
          render: (g) => (
            <div className="min-w-0">
              {/* Name click = guest record in the global right rail (same as
                  the tickets table); the rest of the row opens the badge
                  editor. */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  showInRail({ kind: "guest", guestId: g.id, name: guestDisplayName(g) })
                }}
                className="block max-w-full truncate text-left font-semibold text-plum-900 hover:underline dark:text-plum-50"
              >
                {guestDisplayName(g)}
              </button>
              {/* Title · company — what the badge prints under the name. Blank
                  when there's neither: an email is not a job title. */}
              {(g.titleAtInvite || g.companyAtInvite) && (
                <div className="truncate text-[11px] text-plum-700/55 dark:text-plum-200/40">
                  {[g.titleAtInvite, g.companyAtInvite].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          ),
        },
        {
          // What's on their passport / ticket, for matching at the desk. Only
          // interesting when it isn't already the printed name.
          id: "legalName",
          header: "Legal name",
          sortable: true,
          sortValue: (g) => legalName(g).toLowerCase(),
          render: (g) => {
            const legal = legalName(g)
            if (!legal || legal === guestDisplayName(g))
              return <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            return <>{legal}</>
          },
          minWidth: 140,
          truncate: true,
        },
        {
          id: "tier",
          header: "Ticket",
          basis: "10rem",
          truncate: true,
          sortable: true,
          sortValue: (g) => g.ticketTypeName ?? "",
          render: (g) => g.ticketTypeName ?? "—",
        },
        {
          id: "status",
          header: "Status",
          basis: "7rem",
          sortable: true,
          sortValue: (g) => displayStatus(g),
          render: (g) => (
            <span
              className={`rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_BADGE_CLS[displayStatus(g)] ?? "bg-plum-100 text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55"}`}
            >
              {statusLabel(displayStatus(g))}
            </span>
          ),
        },
        {
          id: "printed",
          header: "Badge printed",
          basis: "8rem",
          sortable: true,
          sortValue: (g) => g.badgePrintedAt ?? "",
          render: (g) =>
            g.badgePrintedAt ? (
              <span className="inline-flex items-center gap-1 text-[12.5px] text-plum-800/80 dark:text-plum-100/70">
                <Printer className="h-3.5 w-3.5" /> {dateTime(g.badgePrintedAt)}
              </span>
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          // Per-row ⋮ menu mirroring the bulk-bar actions.
          id: "actions",
          header: "",
          basis: "3rem",
          align: "right",
          render: (g) => (
            <RowActions
              guest={g}
              onPrint={() => setPrinting([g])}
              onEditNames={() => setBulkEdit([g])}
              onMark={() => void runMarkPrinted([g], false)}
              onClear={() => void runMarkPrinted([g], true)}
            />
          ),
        },
      ],
      groupOptions: [
        {
          id: "printed-date",
          label: "Date printed",
          groupOf: (g) => dateOnly(g.badgePrintedAt) ?? "Not printed",
          // Newest print day first; "Not printed" always sinks to the bottom.
          order: (label) =>
            label === "Not printed" ? Number.MAX_SAFE_INTEGER : -new Date(label).getTime(),
        },
      ],
      filters: tierNames.length
        ? [
            {
              id: "tier",
              label: "Ticket",
              options: [["", "Any"], ...tierNames.map((t) => [t, t] as const)] as const,
              predicate: (value, g) => !value || g.ticketTypeName === value,
            },
          ]
        : undefined,
      savedViews: [
        {
          id: "to-print",
          label: "To print",
          filter: (g) => g.status === "confirmed" && !g.badgePrintedAt,
        },
        { id: "confirmed", label: "Confirmed", filter: (g) => g.status === "confirmed" },
        { id: "printed", label: "Printed", filter: (g) => !!g.badgePrintedAt },
        { id: "all", label: "All", filter: () => true },
      ],
      bulkActions: [
        {
          id: "print",
          label: (rows) => `Print ${rows.length} badge${rows.length === 1 ? "" : "s"}`,
          icon: <Printer className="h-4 w-4" />,
          run: (rows) => setPrinting(rows),
        },
        {
          id: "bulk-edit",
          label: "Edit badge names",
          icon: <Table2 className="h-4 w-4" />,
          run: (rows) => setBulkEdit(rows),
        },
        {
          id: "mark-printed",
          label: "Mark printed",
          icon: <PrinterCheck className="h-4 w-4" />,
          run: (rows) => void runMarkPrinted(rows, false),
        },
        {
          id: "clear-printed",
          label: "Clear print date",
          icon: <PrinterX className="h-4 w-4" />,
          enabled: (rows) => rows.some((g) => g.badgePrintedAt),
          run: (rows) => void runMarkPrinted(rows.filter((g) => g.badgePrintedAt), true),
        },
      ],
      menuActions: [
        {
          id: "badge-settings",
          // Per-event design everyone printing inherits, not a per-print-run
          // choice — edited in the dedicated designer beside this desk view.
          label: "Badge design",
          icon: <Settings2 className="h-4 w-4" />,
          run: () =>
            void navigate({ to: "/$orgSlug/$eventSlug/badge-design", params }),
        },
      ],
      defaultSortId: "name",
      defaultSortDir: "asc",
      // Row click = the dedicated badge editor for that attendee; the NAME
      // link inside the row opens their record in the right reading pane.
      onOpen: (g) => setBadgeEdit(g),
      getRecordHref: (g) => `/${params.orgSlug}/${params.eventSlug}/people/${g.id}`,
      onOpenFullscreen: (g) =>
        void navigate({
          to: "/$orgSlug/$eventSlug/people/$guestId",
          params: { ...params, guestId: g.id },
        }),
      emptyTitle: "No badges to print",
      emptyBlurb: "Confirmed guests without a printed badge will show up here.",
      emptyIcon: <IdCard className="h-5 w-5" />,
    }),
    [tierNames, params, navigate],
  )

  return (
    <div className="relative flex h-full">
      <div className="min-w-0 flex-1">
        <CollectionView
          config={config}
          rows={guests}
          loading={guestsQ.isLoading && guests.length === 0}
          error={
            guestsQ.isError
              ? ((guestsQ.error as Error)?.message ?? "Failed to load guests")
              : null
          }
        />
      </div>
      {badgeEdit && (
        <BadgeEditModal
          guest={badgeEdit}
          eventName={eventRow?.name ?? ""}
          settings={settingsQ.data ?? null}
          fields={attendeeFields}
          valuesByGuest={valuesByGuest}
          guestUrl={(g) =>
            `${window.location.origin}/${params.orgSlug}/${params.eventSlug}/people/${g.id}`
          }
          onPrint={(g) => {
            setBadgeEdit(null)
            setPrinting([g])
          }}
          onClose={() => setBadgeEdit(null)}
        />
      )}
      {bulkEdit && eventId && (
        <BulkEditSheet
          eventId={eventId}
          rows={bulkEdit}
          columns={BADGE_EDIT_COLS}
          showCustomFields={false}
          onClose={() => setBulkEdit(null)}
        />
      )}
      {printing && (
        <BadgeSheet
          rows={printing}
          eventName={eventRow?.name ?? ""}
          // Staff-only check-in identifier (auth-gated record link) — NEVER the
          // event pass: that's a login, and badges are worn in public.
          guestUrl={(g) =>
            `${window.location.origin}/${params.orgSlug}/${params.eventSlug}/people/${g.id}`
          }
          fields={attendeeFields}
          valuesByGuest={valuesByGuest}
          settings={settingsQ.data ?? null}
          onClose={() => setPrinting(null)}
        />
      )}
    </div>
  )
}
